import { FluentProvider, makeStaticStyles, makeStyles, webDarkTheme } from '@fluentui/react-components';
import { createContext, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { get as storageGet, set as storageSet } from '../common/storage';
import { cssVarsFor } from '../theme/themeVars';
import { theme as appLightTheme } from '../theme/tokens';
import { defaultPreferences, type Preferences, type ResolvedTheme, type ThemeMode, type ViewMode } from './models';
import { useSystemTheme } from './useSystemTheme';

const PREFS_KEY = 'nuxni-time-tracker:preferences';

const parseThemeMode = (raw: unknown): ThemeMode =>
    raw === 'dark' || raw === 'light' || raw === 'system' ? raw : defaultPreferences.themeMode;

const loadPreferences = (): Preferences => {
    const raw = storageGet<Partial<Preferences>>(PREFS_KEY);
    if (!raw) {
        return defaultPreferences;
    }
    return {
        defaultView: raw.defaultView === 'list' ? 'list' : 'grid',
        themeMode: parseThemeMode(raw.themeMode),
    };
};

const savePreferences = (next: Preferences): void => storageSet(PREFS_KEY, next);

type PreferencesContextValue = {
    preferences: Preferences;
    // The light/dark actually painted — `themeMode` resolved against the OS.
    resolvedTheme: ResolvedTheme;
    setDefaultView: (next: ViewMode) => void;
    setThemeMode: (next: ThemeMode) => void;
};

const PreferencesContext = createContext<PreferencesContextValue | undefined>(undefined);

export const PreferencesProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const classes = useStyles();
    useGlobalStyles();

    const [preferences, setPreferences] = useState<Preferences>(() => loadPreferences());

    const setDefaultView = useCallback((defaultView: ViewMode) => {
        setPreferences((prev) => {
            const next = { ...prev, defaultView };
            savePreferences(next);
            return next;
        });
    }, []);

    const setThemeMode = useCallback((themeMode: ThemeMode) => {
        setPreferences((prev) => {
            const next = { ...prev, themeMode };
            savePreferences(next);
            return next;
        });
    }, []);

    // `system` follows the OS; an explicit light/dark wins over it.
    const systemTheme = useSystemTheme();
    const resolvedTheme: ResolvedTheme = preferences.themeMode === 'system' ? systemTheme : preferences.themeMode;

    const value = useMemo<PreferencesContextValue>(
        () => ({ preferences, resolvedTheme, setDefaultView, setThemeMode }),
        [preferences, resolvedTheme, setDefaultView, setThemeMode],
    );

    // CSS variables for the active mode are applied inline on the FluentProvider wrapper
    // so switching theme re-paints via cascade without remounting anything.
    const cssVars = useMemo(() => cssVarsFor(resolvedTheme), [resolvedTheme]);
    const fluentTheme = resolvedTheme === 'dark' ? { ...webDarkTheme, fontFamilyBase: appLightTheme.fontFamilyBase } : appLightTheme;

    return (
        <PreferencesContext.Provider value={value}>
            <FluentProvider
                theme={fluentTheme}
                className={classes.container}
                style={cssVars as React.CSSProperties}
                applyStylesToPortals={true}
            >
                {children}
            </FluentProvider>
        </PreferencesContext.Provider>
    );
};

export const usePreferences = (): PreferencesContextValue => {
    const ctx = useContext(PreferencesContext);
    if (!ctx) {
        throw new Error('usePreferences must be used within a PreferencesProvider');
    }
    return ctx;
};

const useStyles = makeStyles({
    container: {
        display: 'contents',
    },
});

const useGlobalStyles = makeStaticStyles({
    body: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        margin: 0,
        fontFamily: appLightTheme.fontFamilyBase,
        fontVariant: 'lining-nums',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
    },
    '#root': {
        display: 'contents',
    },
    ':focus-visible': {
        outline: `2px solid ${appLightTheme.customPaletteGreen}`,
        outlineOffset: '1px',
    },
    'input:focus-visible': {
        outline: 'none',
    },
});
